import clsx from "clsx";
import { forwardRef, memo, useMemo } from "react";
import { Link } from "react-router";

import { Icon } from "@atoms/icon";
import { Typography } from "@atoms/typography";
import {
  ILinkButtonSizeEnum,
  ILinkButtonVariantsEnum,
} from "@molecules/linkButton/types";

import rootClasses from "./styles.module.scss";

import type { ILinkButtonProps } from "@molecules/linkButton/types";

export const LinkButton = memo(
  forwardRef<HTMLButtonElement, ILinkButtonProps>(
    (
      {
        label,
        endIcon,
        startIcon,
        disabled = false,
        hasUnderline = false,
        linkTo,
        onClick,
        isLoading,
        size = ILinkButtonSizeEnum.Medium,
        variant = ILinkButtonVariantsEnum.Black,
        className,
        children,
        ...rootAttributes
      },
      ref,
    ) => {
      const labelComponent = useMemo(
        () => (
          <Typography
            fontType={"inherit"}
            themeColor={"inherit"}
            className={clsx(rootClasses.button__container)}
          >
            {startIcon && <Icon name={startIcon} fontSize={"fs-20"} />}
            {label || undefined}
            {endIcon && <Icon name={endIcon} fontSize={"fs-20"} />}
            {children}
          </Typography>
        ),
        [children, endIcon, label, startIcon],
      );

      const buttonChild = useMemo(
        () => (
          <>
            {linkTo ? (
              <Link
                data-underline={hasUnderline}
                to={linkTo}
                className={clsx(rootClasses.button__link)}
              >
                {labelComponent}
              </Link>
            ) : (
              labelComponent
            )}
          </>
        ),
        [hasUnderline, labelComponent, linkTo],
      );

      return (
        <button
          data-size={size}
          data-variant={variant}
          data-disabled={disabled}
          className={clsx(rootClasses.button, className)}
          ref={ref}
          disabled={disabled}
          onClick={onClick}
          type={"button"}
          {...rootAttributes}
        >
          {isLoading ? "loading" : buttonChild}
        </button>
      );
    },
  ),
);

LinkButton.displayName = "LinkButton";
